import React, { useEffect, useState } from "react";
import { Plus, X, Send, Download, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";

const CONTRACT_STATUS_STYLES = {
  Signed: "bg-emerald-50 text-emerald-700",
  Sent: "bg-blue-50 text-blue-700",
  Draft: "bg-slate-100 text-slate-600",
  Declined: "bg-slate-100 text-slate-600",
};

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ client_id: "", title: "", scope_details: "", price: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [c, cl] = await Promise.all([api.get("/contracts"), api.get("/clients")]);
    setContracts(c.data);
    setClients(cl.data);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contracts", { ...form, price: parseFloat(form.price) || 0 });
      toast.success("Contract created");
      setForm({ client_id: "", title: "", scope_details: "", price: 0 });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const send = async (id) => {
    try {
      await api.post(`/contracts/${id}/send`);
      toast.success("Contract sent to client");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const viewPdf = async (id) => {
    try {
      const { data } = await api.get(`/contracts/${id}/pdf-url`);
      window.open(process.env.REACT_APP_BACKEND_URL + data.url, "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || id;

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="eyebrow mb-2">Pillar 2 · Contracts</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="contracts-title">Contracts</h1>
        </div>
        <button onClick={() => setShowForm(true)} disabled={clients.length === 0} className="bg-slate-900 text-white rounded-full px-5 py-2.5 font-medium hover:bg-slate-700 active:scale-95 disabled:opacity-50 inline-flex items-center gap-2" data-testid="new-contract-button">
          <Plus className="w-4 h-4" /> New contract
        </button>
      </div>

      {clients.length === 0 && (
        <div className="bento p-8 text-center text-slate-500 mb-6">
          Add a client first to create contracts. <Link to="/agency/clients" className="underline text-slate-900">Go to Clients</Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="contracts-list">
        {contracts.map((c) => (
          <div key={c.id} className="bento p-6" data-testid={`contract-card-${c.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="eyebrow mb-2">{clientName(c.client_id)}</p>
                <h3 className="text-xl font-bold text-slate-900">{c.title}</h3>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${CONTRACT_STATUS_STYLES[c.status] || CONTRACT_STATUS_STYLES.Draft}`}>{c.status}</span>
            </div>
            <p className="text-sm text-slate-600 mb-4 line-clamp-2">{c.scope_details}</p>
            <div className="flex items-center justify-between text-sm">
              <span className="font-mono text-slate-900 font-bold">${c.price.toFixed(2)}</span>
              <div className="flex gap-2">
                {c.status === "Draft" && (
                  <button onClick={() => send(c.id)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-100" data-testid={`send-contract-${c.id}`}>
                    <Send className="w-3.5 h-3.5" /> Send
                  </button>
                )}
                {c.pdf_file_id && (
                  <button onClick={() => viewPdf(c.id)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-100" data-testid={`view-pdf-${c.id}`}>
                    <Download className="w-3.5 h-3.5" /> Signed PDF
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {contracts.length === 0 && clients.length > 0 && (
          <div className="bento p-12 text-center text-slate-500 lg:col-span-2">
            <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            No contracts yet.
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={create} className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="new-contract-modal">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tight">New contract</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Client</label>
            <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="contract-client-select">
              <option value="">Select client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
            </select>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="contract-title-input" />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Scope of work</label>
            <textarea required rows={5} value={form.scope_details} onChange={(e) => setForm({ ...form, scope_details: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="contract-scope-input" />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Price (USD)</label>
            <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-6" data-testid="contract-price-input" />
            <button type="submit" disabled={loading} className="w-full bg-slate-900 text-white rounded-full py-3 font-medium hover:bg-slate-700 active:scale-95 disabled:opacity-50" data-testid="create-contract-submit">
              {loading ? "Creating…" : "Create contract"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
